import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiPlus, FiEdit2, FiXCircle, FiTrash2, FiSearch, FiInbox,
  FiBookOpen, FiX, FiUpload, FiFile, FiDownload,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import SearchableSelect from '../../components/SearchableSelect';
import { templateLibraryAPI, parametersAPI, companiesAPI } from '../../services/api';
import { getLocalized } from '../../i18n/getLocalized';
import { formatDate } from '../../utils/dateFormat';
import { saveBlob } from '../../utils/fileDownload';
import './TemplateLibraryAdmin.scss';

const ACCEPT_TYPES = '.doc,.docx,.pdf';

// A contract type is offerable for a company when its scope is "All Companies"
// (empty companyIds) or explicitly includes that company.
const typeInCompany = (ct, companyId) => {
  const ids = (ct.companyIds || []).map(String);
  return ids.length === 0 || ids.includes(String(companyId));
};

// Management screen for the Template Library (Sözleşme Şablon Kütüphanesi).
// Always rendered embedded inside the Contract Settings panel, which supplies
// the page header — same contract as ContractTemplates/SchemaList.
function TemplateLibraryAdmin() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', companyId: '', contractTypeId: '', isActive: true });
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await templateLibraryAPI.list();
      setItems(r.data.items || []);
    } catch {
      toast.error(t('templateLibrary.messages.loadFailed'));
    }
    setLoading(false);
  }, [t]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => {
    companiesAPI.list({ active: 'true' })
      .then(r => setCompanies(r.data.companies || []))
      .catch(() => {});
    parametersAPI.contractTypes.list({ active: 'true' })
      .then(r => setContractTypes(r.data.items || []))
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setEditItem(null);
    setForm({ name: '', companyId: '', contractTypeId: '', isActive: true });
    setFile(null);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      name: item.name || '',
      companyId: item.companyId?._id || '',
      contractTypeId: item.contractTypeId?._id || '',
      isActive: !!item.isActive,
    });
    setFile(null);
    setModalOpen(true);
  };

  const acceptFile = (f) => {
    if (!f) return;
    const ok = /\.(docx?|pdf)$/i.test(f.name);
    if (!ok) { toast.error(t('templateLibrary.validation.fileType')); return; }
    setFile(f);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error(t('templateLibrary.validation.nameRequired'));
    if (!form.companyId) return toast.error(t('templateLibrary.validation.companyRequired'));
    if (!form.contractTypeId) return toast.error(t('templateLibrary.validation.typeRequired'));
    if (!editItem && !file) return toast.error(t('templateLibrary.validation.fileRequired'));

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name.trim());
      formData.append('companyId', form.companyId);
      formData.append('contractTypeId', form.contractTypeId);
      formData.append('isActive', String(form.isActive));
      if (file) formData.append('file', file);

      if (editItem) {
        await templateLibraryAPI.update(editItem._id, formData);
        toast.success(t('templateLibrary.messages.updated'));
      } else {
        await templateLibraryAPI.create(formData);
        toast.success(t('templateLibrary.messages.created'));
      }
      setModalOpen(false);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.error || t('templateLibrary.messages.saveFailed'));
    }
    setSaving(false);
  };

  const handleDeactivate = async (item) => {
    if (!window.confirm(t('templateLibrary.messages.deactivateConfirm', { name: item.name }))) return;
    try {
      await templateLibraryAPI.deactivate(item._id);
      toast.success(t('templateLibrary.messages.deactivated'));
      loadItems();
    } catch {
      toast.error(t('templateLibrary.messages.saveFailed'));
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t('templateLibrary.messages.deleteConfirm', { name: item.name }))) return;
    try {
      await templateLibraryAPI.remove(item._id);
      toast.success(t('templateLibrary.messages.deleted'));
      loadItems();
    } catch {
      toast.error(t('templateLibrary.messages.saveFailed'));
    }
  };

  const handleDownload = async (item) => {
    try {
      const res = await templateLibraryAPI.download(item._id);
      saveBlob(res.data, item.fileId?.name || item.name);
    } catch {
      toast.error(t('templateLibrary.messages.downloadFailed'));
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter(i =>
      (i.name || '').toLowerCase().includes(q)
      || (i.fileId?.name || '').toLowerCase().includes(q)
      || (i.companyId?.name || '').toLowerCase().includes(q)
      || getLocalized(i.contractTypeId?.name, i18n.language).toLowerCase().includes(q))
    : items;
  const activeCount = items.filter(i => i.isActive).length;

  // Contract types offerable under the currently selected company scope.
  const scopedTypes = form.companyId
    ? contractTypes.filter(ct => typeInCompany(ct, form.companyId))
    : [];

  return (
    <div className="tla">
      <div className="content-card">
        <div className="content-card__head">
          <div className="content-card__heading">
            <h3>{t('adminPanel.tabs.templateLibrary')}</h3>
          </div>
          <div className="content-card__actions">
            <div className="search-input">
              <FiSearch />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('templateLibrary.searchPlaceholder')}
              />
            </div>
            <button className="btn btn-primary btn-sm" onClick={openCreate}>
              <FiPlus /> {t('templateLibrary.newTemplate')}
            </button>
          </div>
        </div>

        <div className="content-card__meta">
          <span>
            <strong>{filtered.length}</strong>
            <span>{q ? t('adminPanel.stats.matching') : t('adminPanel.stats.total')}</span>
          </span>
          <span className="divider" />
          <span>
            <span className="badge__dot badge__dot--active" />
            <strong>{activeCount}</strong>
            <span>{t('adminPanel.stats.active')}</span>
          </span>
          <span>
            <span className="badge__dot badge__dot--muted" />
            <strong>{items.length - activeCount}</strong>
            <span>{t('adminPanel.stats.inactive')}</span>
          </span>
        </div>

        <div className="content-card__table">
          {loading ? (
            <div className="state-box">
              <div className="state-box__spinner" />
              <p>{t('common.loading')}</p>
            </div>
          ) : !filtered.length ? (
            <div className="state-box">
              <div className="state-box__icon"><FiInbox /></div>
              <h3>{q ? t('adminPanel.empty.noMatches') : t('templateLibrary.emptyManage.title')}</h3>
              <p>{q ? t('adminPanel.empty.tryDifferent') : t('templateLibrary.emptyManage.desc')}</p>
              {!q && (
                <button className="btn btn-primary btn-sm" onClick={openCreate}>
                  <FiPlus /> {t('templateLibrary.newTemplate')}
                </button>
              )}
            </div>
          ) : (
            <table>
              <thead><tr>
                <th>{t('templateLibrary.columns.name')}</th>
                <th>{t('templateLibrary.columns.company')}</th>
                <th>{t('templateLibrary.columns.contractType')}</th>
                <th>{t('templateLibrary.columns.fileName')}</th>
                <th>{t('templateLibrary.columns.version')}</th>
                <th>{t('templateLibrary.columns.updated')}</th>
                <th>{t('adminPanel.columns.status')}</th>
                <th style={{ width: 128 }}></th>
              </tr></thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item._id}>
                    <td><strong>{item.name}</strong></td>
                    <td>{item.companyId?.name || '—'}</td>
                    <td>{getLocalized(item.contractTypeId?.name, i18n.language) || '—'}</td>
                    <td>{item.fileId?.name || '—'}</td>
                    <td><code>v{item.version || 1}</code></td>
                    <td>{formatDate(item.updatedAt)}</td>
                    <td>
                      <span className={`badge badge-${item.isActive ? 'active' : 'archived'}`}>
                        <span className={`badge__dot badge__dot--${item.isActive ? 'active' : 'muted'}`} />
                        {item.isActive ? t('adminPanel.columns.active') : t('adminPanel.columns.inactive')}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button className="icon-btn" onClick={() => handleDownload(item)} title={t('templateLibrary.download')} aria-label="Download">
                          <FiDownload />
                        </button>
                        <button className="icon-btn" onClick={() => openEdit(item)} title={t('adminPanel.editItem')} aria-label="Edit">
                          <FiEdit2 />
                        </button>
                        {item.isActive && (
                          <button className="icon-btn icon-btn--danger" onClick={() => handleDeactivate(item)} title={t('adminPanel.deactivated')} aria-label="Deactivate">
                            <FiXCircle />
                          </button>
                        )}
                        <button className="icon-btn icon-btn--danger" onClick={() => handleDelete(item)} title={t('templateLibrary.delete')} aria-label="Delete">
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="admin-modal__backdrop" onClick={() => setModalOpen(false)} role="dialog" aria-modal="true">
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <header className="admin-modal__header">
              <div className="admin-modal__title">
                <span className="admin-modal__icon"><FiBookOpen /></span>
                <div>
                  <h3>{editItem ? t('templateLibrary.editTemplate') : t('templateLibrary.newTemplate')}</h3>
                  <p>{t('templateLibrary.modalSubtitle')}</p>
                </div>
              </div>
              <button className="admin-modal__close" onClick={() => setModalOpen(false)} aria-label="Close">
                <FiX />
              </button>
            </header>
            <div className="admin-modal__body">
              <div className="form-section">
                <div className="form-grid">
                  <div className="form-group form-group--full">
                    <label>{t('templateLibrary.form.name')} <span className="req">*</span></label>
                    <input
                      value={form.name}
                      onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      maxLength={200}
                      placeholder={t('templateLibrary.form.namePlaceholder')}
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('templateLibrary.form.company')} <span className="req">*</span></label>
                    <SearchableSelect
                      value={form.companyId}
                      onChange={(v) => setForm(p => ({ ...p, companyId: v, contractTypeId: '' }))}
                      placeholder={t('templateLibrary.form.selectCompany')}
                      required
                      options={companies.map(c => ({ value: c._id, label: c.name }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('templateLibrary.form.contractType')} <span className="req">*</span></label>
                    <SearchableSelect
                      value={form.contractTypeId}
                      onChange={(v) => setForm(p => ({ ...p, contractTypeId: v }))}
                      placeholder={form.companyId
                        ? t('templateLibrary.form.selectContractType')
                        : t('templateLibrary.form.selectCompanyFirst')}
                      disabled={!form.companyId}
                      required
                      options={scopedTypes.map(ct => ({ value: ct._id, label: getLocalized(ct.name, i18n.language) }))}
                    />
                  </div>
                  <div className="form-group form-group--full">
                    <label>
                      {t('templateLibrary.form.document')}
                      {!editItem && <span className="req"> *</span>}
                    </label>
                    {editItem && !file && (
                      <div className="tla-current-file">
                        <FiFile size={14} />
                        <span>{t('templateLibrary.form.currentFile')}: <strong>{editItem.fileId?.name || '—'}</strong> (v{editItem.version || 1})</span>
                      </div>
                    )}
                    {file ? (
                      <div className="tla-file-preview">
                        <FiFile size={16} />
                        <span className="tla-file-preview__name">{file.name}</span>
                        <button type="button" className="icon-btn" onClick={() => setFile(null)} aria-label="Remove">
                          <FiX size={14} />
                        </button>
                      </div>
                    ) : (
                      <label
                        className={`tla-upload ${dragActive ? 'tla-upload--active' : ''}`}
                        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
                        onDragLeave={() => setDragActive(false)}
                        onDrop={e => { e.preventDefault(); setDragActive(false); acceptFile(e.dataTransfer.files?.[0]); }}
                      >
                        <FiUpload size={18} />
                        <span>{editItem ? t('templateLibrary.form.replaceHint') : t('templateLibrary.form.dropOrBrowse')}</span>
                        <em>{t('templateLibrary.form.fileTypes')}</em>
                        <input
                          ref={fileInputRef}
                          type="file"
                          style={{ display: 'none' }}
                          accept={ACCEPT_TYPES}
                          onChange={e => { acceptFile(e.target.files?.[0]); e.target.value = ''; }}
                        />
                      </label>
                    )}
                  </div>
                </div>
                <label className={`toggle ${form.isActive ? 'toggle--on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))}
                  />
                  <span className="toggle__switch"><span className="toggle__thumb" /></span>
                  <span className="toggle__label">{t('adminPanel.fields.active')}</span>
                </label>
              </div>
            </div>
            <div className="admin-modal__footer">
              <button className="btn btn-outline" onClick={() => setModalOpen(false)}>{t('adminPanel.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('adminPanel.saving') : (editItem ? t('adminPanel.update') : t('adminPanel.create'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplateLibraryAdmin;
